import React, { useEffect, useRef } from "react";
import "./ImpactSection.css";

const impactStats = [
  {
    target: 1000000000,
    format: "currency",
    icon: "fas fa-chart-line",
    label: "Assets Managed",
    initial: "₹0",
  },
  {
    target: 800,
    format: "plus",
    icon: "fas fa-users",
    label: "Happy Clients",
    initial: "0+",
  },
  {
    target: 20,
    format: "plus",
    icon: "fas fa-clock",
    label: "Years Experience",
    initial: "0+",
  },
  {
    target: 99,
    format: "percent",
    icon: "fas fa-heart",
    label: "Client Satisfaction",
    initial: "0%",
  },
];

function fmtVal(value, format) {
  if (format === "currency") {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(0)}Cr+`;
    return `₹${value.toLocaleString("en-IN")}`;
  }
  if (format === "percent") return `${value}%`;
  return `${value.toLocaleString()}+`;
}

const ImpactSection = () => {
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const linePathRef = useRef(null);
  const chartCardRef = useRef(null);

  useEffect(() => {
    const revealObs = new IntersectionObserver(
      (entries) =>
        entries.forEach(
          (e) => e.isIntersecting && e.target.classList.add("show"),
        ),
      { threshold: 0.1 },
    );
    if (leftRef.current) revealObs.observe(leftRef.current);
    if (rightRef.current) revealObs.observe(rightRef.current);

    if (linePathRef.current) {
      const len = linePathRef.current.getTotalLength();
      linePathRef.current.style.strokeDasharray = len;
      linePathRef.current.style.strokeDashoffset = len;
    }

    const resetCounts = () => {
      document.querySelectorAll(".wc-ui-impact-stat-value").forEach((el) => {
        const f = el.getAttribute("data-format");
        el.textContent =
          f === "currency" ? "₹0" : f === "percent" ? "0%" : "0+";
      });
    };

    const runCounts = () => {
      document.querySelectorAll(".wc-ui-impact-stat-value").forEach((el) => {
        const tgt = parseInt(el.getAttribute("data-target"), 10);
        const fmt = el.getAttribute("data-format") || "plus";
        let t0 = null;
        const tick = (ts) => {
          if (!t0) t0 = ts;
          const p = Math.min((ts - t0) / 1800, 1);
          el.textContent = fmtVal(
            Math.floor((1 - Math.pow(1 - p, 4)) * tgt),
            fmt,
          );
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    };

    const resetGraph = () => {
      chartCardRef.current?.classList.remove("wc-ui-graph-animate");
      if (linePathRef.current) {
        const len = linePathRef.current.getTotalLength();
        linePathRef.current.style.strokeDasharray = len;
        linePathRef.current.style.strokeDashoffset = len;
      }
    };

    const impactObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            resetCounts();
            resetGraph();
            setTimeout(runCounts, 100);
            setTimeout(
              () => chartCardRef.current?.classList.add("wc-ui-graph-animate"),
              300,
            );
          } else {
            resetCounts();
            resetGraph();
          }
        });
      },
      { threshold: 0.25 },
    );
    if (sectionRef.current) impactObs.observe(sectionRef.current);

    return () => {
      revealObs.disconnect();
      impactObs.disconnect();
    };
  }, []);

  return (
    <section className="wc-ui-impact-section" ref={sectionRef}>
      <div className="wc-ui-impact-container">
        <div className="wc-ui-impact-left wc-ui-reveal" ref={leftRef}>
          <h2 className="wc-ui-impact-title">
            Real Numbers, Real{" "}
            <span className="wc-ui-gradient-text">Impact</span>
          </h2>
          <p className="wc-ui-impact-subtitle">
            Our proven track record speaks for itself. We've helped hundreds of
            clients achieve their financial goals through disciplined planning
            and expert execution.
          </p>
          <div className="wc-ui-impact-stats-grid">
            {impactStats.map((s, i) => (
              <div className="wc-ui-impact-stat-card" key={i}>
                <div className="wc-ui-impact-stat-icon">
                  <i className={s.icon}></i>
                </div>
                <h3
                  className="wc-ui-impact-stat-value"
                  data-target={s.target}
                  data-format={s.format}
                >
                  {s.initial}
                </h3>
                <p className="wc-ui-impact-stat-label">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="wc-ui-impact-right wc-ui-reveal" ref={rightRef}>
          <div className="wc-ui-impact-chart-card" ref={chartCardRef}>
            <div className="wc-ui-impact-chart-header">
              <h3 className="wc-ui-impact-chart-title">
                Portfolio Performance
              </h3>
              <div className="wc-ui-impact-chart-stats">
                <div className="wc-ui-impact-chart-stat">
                  <span className="wc-ui-impact-chart-stat-label">
                    Assets Managed
                  </span>
                  <span className="wc-ui-impact-chart-stat-value">₹100Cr+</span>
                </div>
                <div className="wc-ui-impact-chart-stat">
                  <span className="wc-ui-impact-chart-stat-label">
                    Client Satisfaction
                  </span>
                  <span className="wc-ui-impact-chart-stat-value">99%</span>
                </div>
              </div>
            </div>
            <div className="wc-ui-impact-chart-area">
              <svg
                className="wc-ui-impact-chart-svg"
                viewBox="0 0 600 300"
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <linearGradient
                    id="wc-line-grad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#2563eb" />
                    <stop offset="100%" stopColor="#10b981" />
                  </linearGradient>
                  <linearGradient
                    id="wc-area-grad"
                    x1="0%"
                    y1="0%"
                    x2="0%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#2563eb" stopOpacity="0.3" />
                    <stop
                      offset="100%"
                      stopColor="#10b981"
                      stopOpacity="0.05"
                    />
                  </linearGradient>
                </defs>
                <g>
                  {[40, 90, 140, 190, 240].map((y) => (
                    <line
                      key={y}
                      x1="60"
                      y1={y}
                      x2="560"
                      y2={y}
                      stroke="rgba(255,255,255,0.1)"
                    />
                  ))}
                </g>
                <path
                  className="wc-ui-impact-area-fill"
                  d="M 100 220 L 200 160 L 300 120 L 400 80 L 500 40 L 500 240 L 100 240 Z"
                  fill="url(#wc-area-grad)"
                />
                <path
                  className="wc-ui-impact-line-path"
                  d="M 100 220 L 200 160 L 300 120 L 400 80 L 500 40"
                  fill="none"
                  stroke="url(#wc-line-grad)"
                  strokeWidth="4"
                  ref={linePathRef}
                />
                <g>
                  {[
                    [100, 220],
                    [200, 160],
                    [300, 120],
                    [400, 80],
                    [500, 40],
                  ].map(([cx, cy], i) => (
                    <circle
                      key={i}
                      cx={cx}
                      cy={cy}
                      r="6"
                      className="wc-ui-impact-data-point"
                    />
                  ))}
                </g>
                <g className="wc-ui-impact-x-labels">
                  {["2021", "2022", "2023", "2024", "2025"].map((yr, i) => (
                    <text key={yr} x={100 + i * 100} y="270">
                      {yr}
                    </text>
                  ))}
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactSection;
